import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Columns2, LayoutList, Printer, ZoomIn, ZoomOut } from 'lucide-react';
import { Capa, PaginaMiolo } from './pagina-documento';
import type { DocumentoMontado } from './montar-documento';
import type { ManualData } from '../manual-types';

const LARGURA_A4 = 595;
const ALTURA_A4 = 842;
const ESPACO = 24;

export type ModoPrevia = 'pagina' | 'rolagem';

export function PainelPrevia({ dados, documento, aoImprimir }: {
  dados: ManualData;
  documento: DocumentoMontado;
  aoImprimir: () => void;
}) {
  const [modo, setModo] = useState<ModoPrevia>('pagina');
  const [indice, setIndice] = useState(0);
  const [zoom, setZoom] = useState(0.82);
  const palco = useRef<HTMLDivElement>(null);

  const total = documento.totalPaginas + 1; // capa + miolo
  const seguro = Math.min(indice, total - 1);

  useEffect(() => {
    if (indice > total - 1) setIndice(Math.max(0, total - 1));
  }, [indice, total]);

  const capitulos = useMemo(
    () => documento.sumario.filter((entrada) => entrada.nivel === 1),
    [documento.sumario],
  );

  const irPara = (destino: number) => {
    const alvo = Math.max(0, Math.min(total - 1, destino));
    setIndice(alvo);
    if (modo === 'rolagem' && palco.current) {
      const folha = palco.current.querySelector<HTMLElement>(`[data-folha="${alvo}"]`);
      folha?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const folhas = modo === 'rolagem'
    ? Array.from({ length: total }, (_, posicao) => posicao)
    : [seguro];

  const alturaConteudo = folhas.length * ALTURA_A4 + (folhas.length - 1) * ESPACO;

  return (
    <section className="manual-preview-panel">
      <div className="manual-preview-toolbar">
        <div className="manual-preview-info">
          <strong>Prévia do documento</strong>
          <span>A4 retrato · {documento.totalPaginas + 1} páginas · paginação automática</span>
        </div>

        <div className="manual-preview-controles">
          <div className="manual-preview-grupo" role="group" aria-label="Modo de visualização">
            <button
              type="button"
              className={modo === 'pagina' ? 'is-active' : ''}
              title="Página a página"
              onClick={() => setModo('pagina')}
            >
              <Columns2 />
            </button>
            <button
              type="button"
              className={modo === 'rolagem' ? 'is-active' : ''}
              title="Rolagem contínua"
              onClick={() => setModo('rolagem')}
            >
              <LayoutList />
            </button>
          </div>

          <div className="manual-preview-grupo">
            <button type="button" title="Reduzir" onClick={() => setZoom((valor) => Math.max(0.4, Number((valor - 0.1).toFixed(2))))}>
              <ZoomOut />
            </button>
            <span className="manual-preview-zoom">{Math.round(zoom * 100)}%</span>
            <button type="button" title="Ampliar" onClick={() => setZoom((valor) => Math.min(1.6, Number((valor + 0.1).toFixed(2))))}>
              <ZoomIn />
            </button>
          </div>

          <div className="manual-preview-grupo">
            <button type="button" title="Página anterior" disabled={seguro === 0} onClick={() => irPara(seguro - 1)}>
              <ChevronLeft />
            </button>
            <select
              value={seguro}
              aria-label="Ir para a página"
              onChange={(evento) => irPara(Number(evento.target.value))}
            >
              <option value={0}>Capa</option>
              {documento.paginas.map((pagina, posicao) => (
                <option key={pagina.id} value={posicao + 1}>Página {pagina.numero}</option>
              ))}
            </select>
            <button type="button" title="Próxima página" disabled={seguro === total - 1} onClick={() => irPara(seguro + 1)}>
              <ChevronRight />
            </button>
          </div>

          <button type="button" className="manual-primary-button is-compact" onClick={aoImprimir}>
            <Printer />Gerar PDF
          </button>
        </div>
      </div>

      {capitulos.length > 0 && (
        <nav className="manual-preview-atalhos" aria-label="Ir para capítulo">
          {capitulos.map((capitulo) => (
            <button
              key={`${capitulo.rotulo}-${capitulo.pagina}`}
              type="button"
              onClick={() => {
                const posicao = documento.paginas.findIndex((pagina) => pagina.numero === capitulo.pagina);
                if (posicao >= 0) irPara(posicao + 1);
              }}
            >
              {capitulo.rotulo}
            </button>
          ))}
        </nav>
      )}

      <div className="manual-preview-stage" ref={palco}>
        <div
          className="manual-preview-escala"
          style={{ width: LARGURA_A4 * zoom, height: alturaConteudo * zoom }}
        >
          <div className="manual-preview-folhas" style={{ transform: `scale(${zoom})` }}>
            {folhas.map((posicao) => (
              <div key={posicao} data-folha={posicao} className="manual-preview-folha">
                {posicao === 0
                  ? <Capa dados={dados} />
                  : (
                    <PaginaMiolo
                      dados={dados}
                      pagina={documento.paginas[posicao - 1]}
                      total={documento.totalPaginas}
                    />
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
